import { cognitoDomainConfig, cognitoOIDCConfig } from '@/config/cognito.config';
import { UserManager, type User } from 'oidc-client-ts';

// ユーザー情報インターフェース
export interface CognitoUser {
  id_token: string;
  profile: {
    'email'?: string;
    'custom:employeeId'?: string;
    'custom:prctCode'?: string;
  };
  expires_at?: number;
}

export class OIDCAuthService {
  private static instance: OIDCAuthService;
  private userManager: UserManager;
  private currentUser: User | null = null;

  private constructor() {
    // 公式ドキュメントの例に基づいてUserManagerインスタンスを作成
    this.userManager = new UserManager({
      ...cognitoOIDCConfig,
    });

    // イベントリスナーを設定
    this.setupEventListeners();
  }

  public static getInstance(): OIDCAuthService {
    if (!OIDCAuthService.instance) {
      OIDCAuthService.instance = new OIDCAuthService();
    }
    return OIDCAuthService.instance;
  }

  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    // ユーザー読み込みイベント
    this.userManager.events.addUserLoaded((user: User) => {
      this.currentUser = user;
    });

    // ユーザーアンロードイベント
    this.userManager.events.addUserUnloaded(() => {
      console.log('ユーザーがログアウトしました');
      this.currentUser = null;
    });
  }

  /**
   * ログインリダイレクトを開始 - 公式ドキュメントの例に基づく
   */
  async signIn(): Promise<void> {
    try {
      await this.userManager.signinRedirect();
    } catch (error) {
      console.error('ログインリダイレクトに失敗:', error);
      throw error;
    }
  }
  /**
   * 全ての認証状態をクリア
   */
  async clearAllAuthState(): Promise<void> {
    try {
      // UserManagerの状態をクリア
      await this.userManager.removeUser();
      await this.userManager.clearStaleState();

      // 現在のユーザー状態をクリア
      this.currentUser = null;

      console.log('全ての認証状態がクリアされました');
    } catch (error) {
      console.warn('認証状態のクリア中にエラー:', error);
    }
  }

  /**
   * ログインコールバックを処理 - 公式ドキュメントの例に基づく
   */
  async handleSigninCallback(): Promise<User | null> {
    try {
      const user = await this.userManager.signinCallback();
      this.currentUser = user || null;
      return user || null;
    } catch (error) {
      console.error('ログインコールバックの処理に失敗:', error);
      throw error;
    }
  }

  /**
   * ログアウトリダイレクト - 公式ドキュメントの例に基づく
   */
  async signOut(): Promise<void> {
    const logoutUri = cognitoOIDCConfig.post_logout_redirect_uri;
    const cognitoDomain = cognitoDomainConfig.domain;
    const clientId = cognitoDomainConfig.clientId;
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  }

  /**
   * ログアウトコールバックを処理
   */
  async handleSignoutCallback(): Promise<void> {
    try {
      await this.userManager.signoutCallback();
      this.currentUser = null;
      console.log('ログアウトコールバックの処理が成功しました');
    } catch (error) {
      console.error('ログアウトコールバックの処理に失敗:', error);
      throw error;
    }
  }

  /**
   * 現在のユーザーを取得
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      if (this.currentUser && !this.currentUser.expired) {
        return this.currentUser;
      }

      const user = await this.userManager.getUser();
      this.currentUser = user || null;
      return user || null;
    } catch (error) {
      console.error('現在のユーザーの取得に失敗:', error);
      return null;
    }
  }

  /**
   * ユーザーが認証済みかどうかをチェック
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return user !== null && !user.expired;
    } catch (error) {
      console.error('認証状態のチェックに失敗:', error);
      return false;
    }
  }

  /**
   * IDトークンを取得
   */
  async getIdToken(): Promise<string | null> {
    try {
      const user = await this.getCurrentUser();
      return user?.id_token || null;
    } catch (error) {
      console.error('IDトークンの取得に失敗:', error);
      return null;
    }
  }

  /**
   * ユーザー情報を取得
   */
  async getUserInfo(): Promise<CognitoUser | null> {
    try {
      const user = await this.getCurrentUser();
      if (!user || !user.id_token) return null;

      return {
        id_token: user.id_token,
        profile: user.profile,
        expires_at: user.expires_at,
      };
    } catch (error) {
      console.error('ユーザー情報の取得に失敗:', error);
      return null;
    }
  }

  /**
   * 保存されたセッションデータをクリア
   */
  async clearStaleState(): Promise<void> {
    try {
      await this.userManager.clearStaleState();
    } catch (error) {
      console.error('期限切れ状態のクリアに失敗:', error);
    }
  }

  /**
   * ユーザーデータを削除
   */
  async removeUser(): Promise<void> {
    try {
      await this.userManager.removeUser();
      this.currentUser = null;
    } catch (error) {
      console.error('ユーザーデータの削除に失敗:', error);
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export const oidcAuthService = OIDCAuthService.getInstance();
